/**
 * linkTipPlaces — given an insider-tip string and a list of place names
 * (from the day's activities/meals), return a segmented array suitable for
 * rendering with clickable links on the matched place names.
 *
 * Each segment is either:
 *   { type: 'text', text: string }
 *   { type: 'place', text: string, place: { name, category, placeData, item } }
 *
 * Matching rules:
 *   - Case-insensitive whole-word match (handles punctuation)
 *   - Longest names matched first (avoids "Belem" eating "Pastéis de Belém")
 *   - Each place is linked at most once per tip (first occurrence)
 *   - Minimum 3 characters to avoid false positives on short words
 */

/** Escape a string for use in a regex. */
function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Build the candidate place list from a day's activities and meals.
 * Returns: Array<{ name, category, placeData, item }>
 */
export function collectDayPlaces(day) {
  if (!day || typeof day !== 'object') return [];
  const places = [];
  const seen = new Set();
  const push = (name, category, item) => {
    if (!name || typeof name !== 'string') return;
    const trimmed = name.trim();
    if (trimmed.length < 3) return;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    places.push({
      name: trimmed,
      category,
      placeData: item?.placeData || null,
      item: item || null,
    });
  };
  // Activities may live under `activities` (legacy/web) or `schedule`
  // (engine-normalized / mobile). Support both.
  const activitySources = [
    ...(Array.isArray(day.activities) ? day.activities : []),
    ...(Array.isArray(day.schedule) ? day.schedule : []),
  ];
  activitySources.forEach((a) => {
    push(a?.activity || a?.name || a?.title, 'activity', a);
  });
  (day.meals || []).forEach((m) => {
    push(m?.restaurant || m?.name || m?.title, 'dining', m);
  });
  // Hotel(s) if exposed on the day
  if (day.hotel?.name) push(day.hotel.name, 'hotel', day.hotel);
  if (Array.isArray(day.hotels)) day.hotels.forEach((h) => push(h?.name, 'hotel', h));
  return places;
}

/**
 * Split a tip text into segments, highlighting mentions of places.
 *
 * @param {string} tipText
 * @param {Array<{name, category, placeData, item}>} places
 * @returns {Array<{type:'text'|'place', text:string, place?:object}>}
 */
export function linkTipPlaces(tipText, places) {
  if (!tipText || typeof tipText !== 'string') return [{ type: 'text', text: '' }];
  if (!Array.isArray(places) || places.length === 0) {
    return [{ type: 'text', text: tipText }];
  }

  // Sort longest first so compound names win
  const sorted = [...places].sort((a, b) => b.name.length - a.name.length);

  // Find all match ranges, preferring longer matches, linking each place at most once.
  const ranges = [];
  const usedPlaces = new Set();
  for (const p of sorted) {
    if (usedPlaces.has(p.name.toLowerCase())) continue;
    // Whole-word match: boundary = start/end, whitespace, or common punctuation.
    const pattern = new RegExp(
      `(^|[\\s\\p{P}])(${escapeRegex(p.name)})(?=$|[\\s\\p{P}])`,
      'iu'
    );
    const m = pattern.exec(tipText);
    if (!m) continue;
    const start = m.index + m[1].length;
    const end = start + m[2].length;
    // Skip if this range overlaps an already-captured range
    const overlaps = ranges.some((r) => !(end <= r.start || start >= r.end));
    if (overlaps) continue;
    ranges.push({ start, end, place: p, text: m[2] });
    usedPlaces.add(p.name.toLowerCase());
  }

  if (ranges.length === 0) return [{ type: 'text', text: tipText }];

  ranges.sort((a, b) => a.start - b.start);

  const segments = [];
  let cursor = 0;
  for (const r of ranges) {
    if (r.start > cursor) {
      segments.push({ type: 'text', text: tipText.slice(cursor, r.start) });
    }
    segments.push({ type: 'place', text: r.text, place: r.place });
    cursor = r.end;
  }
  if (cursor < tipText.length) {
    segments.push({ type: 'text', text: tipText.slice(cursor) });
  }
  return segments;
}

export default linkTipPlaces;
